import React, { useState, useEffect, ChangeEvent } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Switch,
  FormControlLabel,
} from "@mui/material";
import * as Yup from "yup";

export type DiscountStatus = "ACTIVE" | "INACTIVE" | "EXPIRED";
export type DiscountOn = "PRICE" | "CREDITS" | "BOTH";
export type DiscountType = "PERCENTAGE" | "FIXED_AMOUNT";

export interface UpdatePlanDiscountPayload {
  discount_on: string | null;
  discount_type: string | null;
  discount_value?: number | null;
  credit_bonus?: number | null;
  start_at?: string | null;
  end_at?: string | null;
  status?: DiscountStatus;
  max_usage?: number | null;
  max_usage_per_user?: number | null;
  tnc?: string | null;
  can_apply?: boolean;
}

interface UpdatePlanDiscountProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: UpdatePlanDiscountPayload, params: string) => void;
  discountId: number;
  initialData: UpdatePlanDiscountPayload;
}

const schema = Yup.object().shape({
  discount_on: Yup.mixed<DiscountOn>()
    .oneOf(["PRICE", "CREDITS", "BOTH"], "Invalid discount target")
    .required("Discount target is required"),

  discount_type: Yup.mixed<DiscountType>()
    .oneOf(["PERCENTAGE", "FIXED_AMOUNT"], "Invalid discount type")
    .required("Discount type is required"),

  discount_value: Yup.number()
    .min(0, "Must be ≥ 0")
    .required("Discount value is required"),

  credit_bonus: Yup.number()
    .nullable()
    .min(0, "Must be ≥ 0")
    .when("discount_on", (discount_on, schema) => {
      const discount = discount_on as unknown as DiscountOn;
      if (discount === "CREDITS" || discount === "BOTH") {
        return schema.required(
          "Credit bonus is required when discount on credits"
        );
      }
      if (discount === "PRICE") {
        return schema.test(
          "is-null",
          "Credit bonus must be empty when discount on price",
          (val) => val == null
        );
      }
      return schema.nullable();
    }),
  start_at: Yup.date()
    .required("Start date is required")
    .typeError("Invalid start date"),

  end_at: Yup.date()
    .min(Yup.ref("start_at"), "End must be after start")
    .required("End date is required")
    .typeError("Invalid end date"),

  status: Yup.mixed<DiscountStatus>()
    .oneOf(["ACTIVE", "INACTIVE", "EXPIRED"], "Invalid status")
    .required("Status is required"),

  max_usage: Yup.number()
    .min(0, "Must be ≥ 0")
    .required("Max usage is required"),

  max_usage_per_user: Yup.number()
    .min(0, "Must be ≥ 0")
    .required("Max usage per user is required"),

  tnc: Yup.string().required("Terms & Conditions are required"),

  can_apply: Yup.boolean().required(
    "Please specify if discount can be applied"
  ),
});

const UpdatePlanDiscount: React.FC<UpdatePlanDiscountProps> = ({
  open,
  onClose,
  onSubmit,
  discountId,
  initialData,
}) => {
  const toLocal = (iso?: string | null) => {
    if (!iso) return "";
    const d = new Date(iso);
    const tzMs = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - tzMs).toISOString().slice(0, 16);
  };

  const [vals, setVals] = useState({
    discount_on: "",
    discount_type: "",
    discount_value: "",
    credit_bonus: "",
    start_at: "",
    end_at: "",
    status: "ACTIVE" as DiscountStatus,
    max_usage: "",
    max_usage_per_user: "",
    tnc: "",
    can_apply: false,
  });
  const [errs, setErrs] = useState<
    Partial<Record<keyof UpdatePlanDiscountPayload, string>>
  >({});

  useEffect(() => {
    setVals({
      discount_on: initialData.discount_on?.toString() ?? "",
      discount_type: initialData.discount_type?.toString() ?? "",
      discount_value: initialData.discount_value?.toString() ?? "",
      credit_bonus: initialData.credit_bonus?.toString() ?? "",
      start_at: toLocal(initialData.start_at),
      end_at: toLocal(initialData.end_at),
      status: initialData.status ?? "ACTIVE",
      max_usage: initialData.max_usage?.toString() ?? "",
      max_usage_per_user: initialData.max_usage_per_user?.toString() ?? "",
      tnc: initialData.tnc ?? "",
      can_apply: initialData.can_apply ?? false,
    });
    setErrs({});
  }, [initialData, open]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    let v: any = type === "checkbox" ? checked : value;
    if (type === "number") v = value === "" ? "" : Number(value).toString();
    setVals((p) => ({ ...p, [name]: v }));
  };

  const handleSelect = (e: any) => {
    const { name, value } = e.target;
    setVals((p) => ({ ...p, [name]: value }));
  };

  const handleSubmit = async () => {
    try {
      const raw: UpdatePlanDiscountPayload = {
        discount_on: vals.discount_on || null,
        discount_type: vals.discount_type || null,
        discount_value:
          vals.discount_value !== "" ? Number(vals.discount_value) : null,
        credit_bonus:
          vals.credit_bonus !== "" ? Number(vals.credit_bonus) : null,
        start_at: vals.start_at ? new Date(vals.start_at).toISOString() : null,
        end_at: vals.end_at ? new Date(vals.end_at).toISOString() : null,
        status: vals.status,
        max_usage: vals.max_usage !== "" ? Number(vals.max_usage) : null,
        max_usage_per_user:
          vals.max_usage_per_user !== ""
            ? Number(vals.max_usage_per_user)
            : null,
        tnc: vals.tnc || null,
        can_apply: vals.can_apply,
      };

      const valid = await schema.validate(raw, { abortEarly: false });

      setErrs({});

      const payload: UpdatePlanDiscountPayload = {
        ...valid,
        start_at: valid.start_at
          ? (valid.start_at as Date).toISOString()
          : null,
        end_at: valid.end_at ? (valid.end_at as Date).toISOString() : null,
      };

      onSubmit(payload, `${discountId}/`);
    } catch (err: any) {
      const fe: any = {};
      err.inner?.forEach((e: any) => {
        fe[e.path] = e.message;
      });
      setErrs(fe);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogTitle>Update Plan Discount</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <FormControl fullWidth error={Boolean(errs.discount_on)}>
            <InputLabel>Discount On</InputLabel>
            <Select
              name="discount_on"
              value={vals.discount_on}
              onChange={handleSelect}
              label="Discount On"
            >
              <MenuItem value="PRICE">Price</MenuItem>
              <MenuItem value="CREDITS">Credits</MenuItem>
              <MenuItem value="BOTH">Both</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth error={Boolean(errs.discount_type)}>
            <InputLabel>Type</InputLabel>
            <Select
              name="discount_type"
              value={vals.discount_type}
              onChange={handleSelect}
              label="Type"
            >
              <MenuItem value="PERCENTAGE">Percentage</MenuItem>
              <MenuItem value="FIXED_AMOUNT">Fixed Amount</MenuItem>
            </Select>
          </FormControl>

          <TextField
            name="discount_value"
            label="Discount Value"
            type="number"
            inputProps={{ min: 0 }}
            value={vals.discount_value}
            onChange={handleChange}
            error={Boolean(errs.discount_value)}
            helperText={errs.discount_value}
            fullWidth
          />

          <TextField
            name="credit_bonus"
            label="Credit Bonus"
            type="number"
            inputProps={{ min: 0 }}
            value={vals.credit_bonus ?? ""}
            onChange={handleChange}
            error={Boolean(errs.credit_bonus)}
            helperText={errs.credit_bonus}
            fullWidth
          />

          <TextField
            name="start_at"
            label="Start At"
            type="datetime-local"
            InputLabelProps={{ shrink: true }}
            value={vals.start_at}
            onChange={handleChange}
            error={Boolean(errs.start_at)}
            helperText={errs.start_at}
            fullWidth
          />

          <TextField
            name="end_at"
            label="End At"
            type="datetime-local"
            InputLabelProps={{ shrink: true }}
            value={vals.end_at}
            onChange={handleChange}
            error={Boolean(errs.end_at)}
            helperText={errs.end_at}
            fullWidth
          />

          <FormControlLabel
            control={
              <Switch
                name="can_apply"
                checked={vals.can_apply}
                onChange={handleChange}
              />
            }
            label="Can Apply"
          />

          <FormControl fullWidth error={Boolean(errs.status)}>
            <InputLabel>Status</InputLabel>
            <Select
              name="status"
              value={vals.status}
              onChange={handleSelect}
              label="Status"
            >
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="INACTIVE">Inactive</MenuItem>
              <MenuItem value="EXPIRED">Expired</MenuItem>
            </Select>
          </FormControl>

          <TextField
            name="max_usage"
            label="Max Usage"
            type="number"
            inputProps={{ min: 0 }}
            value={vals.max_usage}
            onChange={handleChange}
            error={Boolean(errs.max_usage)}
            helperText={errs.max_usage}
            fullWidth
          />

          <TextField
            name="max_usage_per_user"
            label="Max Usage/User"
            type="number"
            inputProps={{ min: 0 }}
            value={vals.max_usage_per_user}
            onChange={handleChange}
            error={Boolean(errs.max_usage_per_user)}
            helperText={errs.max_usage_per_user}
            fullWidth
          />

          <TextField
            name="tnc"
            label="Terms & Conditions"
            value={vals.tnc}
            onChange={handleChange}
            error={Boolean(errs.tnc)}
            helperText={errs.tnc}
            multiline
            rows={3}
            fullWidth
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" color="primary">
          Update
        </Button>
      </DialogActions>
    </Dialog>
  );
};
export default UpdatePlanDiscount;
